import { FC, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useAppSelector } from '../../store/hooks'
import {
  UpdateAccountShipment,
  createShipmentApi,
  getShipmentsExists,
} from '../../services'

import {
  AccountInterface,
  BusinessUnitInterface,
  CoordinatesInterface,
  DeliveryEnum,
  FileError,
  ItemInterface,
  PaymentModeId,
  PieceInterface,
  PreShipmentInterface,
  RangeDeclareDto,
  ServiceDTO,
  ShipmentInterface,
  ShipmentStatusEnum,
  ShippingMethodEnum,
  StoreShipmentInterface,
  TrackingEnum,
} from '../../interfaces'
import {
  Card,
  MainPage,
  PageTitle,
  SenderForm,
  ConsigneeFormV2,
  AccountDetails,
  ShipmentFormV2,
  ShipmentMessageModal,
  ConfirmModalStatus,
  ShipmentPrintModal,
  ModalLoading,
} from '../../components'
import { useContractDestinationBUs } from '../../hooks/useContractDestinationBUs'
import { resolveDestinationBusinessUnits } from '../../utils/destinationBusinessUnits'
import { PackageTypeEnum } from '../../interfaces/Shipment/PackageTypeEnum'
import { WeightTypeEnum } from '../../interfaces/Shipment/WeightTypeEnum'
import { AccountShipmentDto } from '../../interfaces/Account/AccountInterfaces'
import { ScrollToError } from '../../components/Atoms/ScrollToFieldError'
import { ShipmentService } from '../../interfaces/Services/ServicesEnum'
import { mapPreShipmentToStoreShipment } from '../../utils/mappers'

const CreateShipmentV2: FC = () => {
  const rangeMinDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  )

  const validateTracking = async (tracking: string) => {
    if (!tracking.trim()) {
      return true
    }

    let accountId = user?.client?.id ?? ''
    let accountCode = user?.client?.accountCode ?? ''

    const result = await getShipmentsExists(
      tracking,
      accountCode,
      accountCode == '' ? accountId : undefined
    )
    if (result != tracking) {
      return true
    } else {
      return false
    }
  }

  const newPieceInitialValues = {
    category: '',
    height: '',
    width: '',
    length: '',
    weight: '',
    declaredValue: '0',
    amount: 1,
    packageType: '20',
  }

  const validationSchema = Yup.object().shape({
    sender: Yup.object().shape({
      fullName: Yup.string().required('Este campo es requerido'),
      abbreviationName: Yup.object().required('Este campo es requerido'),
      identificationNumber: Yup.string().required('Este campo es requerido'),
    }),
    consignee: Yup.object().shape({
      fullName: Yup.string().required('Este campo es requerido'),
      abbreviationName: Yup.object().required('Este campo es requerido'),
      identificationNumber: Yup.string().required('Este campo es requerido'),
      phoneNumber: Yup.string()
        .required('Este campo es requerido')
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 7 caracteres',
          test: (number, parent) => {
            if (parent.parent.codePhoneNumber !== 'FIJO' && number.length > 7)
              return false

            return true
          },
        })
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 10 caracteres',
          test: (number, parent) => {
            if (parent.parent.codePhoneNumber === 'FIJO' && number.length > 10)
              return false

            return true
          },
        }),
      codePhoneNumber: Yup.string().required('Este campo es requerido'),
      cellPhoneNumber: Yup.string()
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 7 caracteres',
          test: (number, parent) => {
            if (
              parent.parent.codecellNumber !== 'FIJO' &&
              number !== undefined &&
              number?.length > 7
            )
              return false

            return true
          },
        })
        .test({
          name: 'number',
          message: 'Debe tener un maximo de 10 caracteres',
          test: (number, parent) => {
            if (
              parent.parent.codecellNumber === 'FIJO' &&
              number !== undefined &&
              number?.length > 10
            )
              return false

            return true
          },
        }),
      email: Yup.string()
        .email('Correo invalido. Ejemplo: tealca@tealca.com')
        .required('Este campo es requerido'),
      city: Yup.string().required('Este campo es requerido'),
      address: Yup.string().required('Este campo es requerido'),
      businessUnit: Yup.object().required('Este campo es requerido'),
      accountSelected: Yup.object().required('Este campo es requerido'),
      ShipmentType: Yup.string().required('Este campo es requerido'),
    }),
    shipment: Yup.object().shape({
      pieces: Yup.array()
        .test({
          name: 'pieces',
          message: 'Debe agregar al menos una pieza',
          test: (pieces) => (pieces?.length ?? 0) > 0,
        })
        .test({
          name: 'pieces-weight',
          skipAbsent: true,
          test: function (pieces, context) {
            const { serviceData } = context.parent // Asumiendo que guardaste el servicio aquí
            if (!serviceData || !pieces) return true

            const sum = pieces.reduce((acc, piece) => acc + piece.weight, 0)
            const min = serviceData.minWeight ?? 0
            const max = serviceData.maxWeight ?? Infinity

            if (sum < min) {
              return context.createError({
                message: `El peso total debe ser mayor o igual a ${min}kg`,
              })
            }

            if (sum > max) {
              return context.createError({
                message: `El peso total debe ser menor o igual a ${max}kg`,
              })
            }

            return true
          },
        }),
      valueDeclarate: Yup.string().when('isSafeKeeping', {
        is: (isSafeKeeping: boolean) => isSafeKeeping,
        then: () =>
          Yup.string()
            .required('Este campo es requerido')
            // .test({
            //   name: 'declared-min-value',
            //   message: 'El valor declarado debe ser mayor a 0.01',
            //   test: (value) => {
            //     const numericValue = parseFloat(value || '0')
            //     return numericValue > 0
            //   },
            // })
            .test({
              name: 'declared',
              skipAbsent: true,
              test: (value, parent) => {
                if (
                  (parent.parent.paymentModeID as PaymentModeId)?.toString() ===
                  PaymentModeId.CREDIT?.toString()
                ) {
                  if (
                    (parent.parent.service as ShipmentService)?.toString() ===
                    ShipmentService.DOCUMENT?.toString()
                  ) {
                    const totalChargedWeight =
                      parent.parent?.totalChargedWeight ?? 0

                    let totalValue = 0
                    totalValue = minDeclaredValue(
                      totalChargedWeight ?? 0,
                      rangeMinDeclareList,
                      parent.parent.service
                    )

                    const numericValue = parseFloat(value || '0')

                    if (numericValue < totalValue) {
                      return parent.createError({
                        message: `El valor declarado no puede ser menor a ${totalValue.toFixed(
                          2
                        )}$`,
                      })
                    }
                  } else if (parseFloat(value || '0') >= 1) return true
                  else
                    return parent.createError({
                      message: `El valor declarado no puede ser mayor o igual a 1$`,
                    })
                }

                const totalChargedWeight =
                  parent.parent?.totalChargedWeight ?? 0

                let totalValue = 0
                totalValue = minDeclaredValue(
                  totalChargedWeight ?? 0,
                  rangeMinDeclareList,
                  parent.parent.service
                )
                const numericValue = parseFloat(value || '0')

                if (numericValue < totalValue) {
                  return parent.createError({
                    message: `El valor declarado no puede ser menor a ${totalValue.toFixed(
                      2
                    )}$`,
                  })
                }

                return true
              },
            }),
      }),
      declaratedValueInvoceNum: Yup.string().when(
        ['valueDeclarate', 'isSafeKeeping'],
        {
          is: (valueDeclarate: string, isSafeKeeping: boolean) => {
            const numericValue = parseFloat(valueDeclarate || '0')
            return isSafeKeeping && numericValue >= 1500
          },
          then: () =>
            Yup.string().required(
              'El número de factura es requerido para valores declarados mayores a 1500$'
            ),
        }
      ),
      observations: Yup.string().max(150, 'maximo 150 caracteres'),
      service: Yup.string().required('El servicio es Requerido'),
      fileUpload: Yup.mixed().test(
        'fileValidation',
        'Error en archivos',
        (value, context) => {
          if (!value) return true

          const files = Array.isArray(value) ? value : [value]
          const errors: FileError[] = []

          // Validación de cantidad máxima de archivos
          if (files.length > 5) {
            errors.push({
              fileName: 'Cantidad de archivos',
              fileSize: 0,
              message: `Máximo 5 archivos permitidos`,
            })
          }

          files.forEach((file) => {
            if (file.size > 5 * 1024 * 1024) {
              errors.push({
                fileName: file.name,
                fileSize: file.size,
                message: `El archivo excede 5MB`,
              })
            }

            if (file.name.length > 60) {
              errors.push({
                fileName: file.name,
                fileSize: file.size,
                message: `El nombre excede 60 caracteres`,
              })
            }
          })

          if (errors.length > 0) {
            return context.createError({ message: JSON.stringify(errors) })
          }
          return true
        }
      ),
      tracking: Yup.string().test(
        'numberExists',
        'Nº de guía aliada ya existe, por favor ingrese uno diferente',
        async (value) => {
          const isValid = await validateTracking(value || '')
          // Forzamos a que sea boolean (true/false) y nunca undefined
          return isValid === true
        }
      ),
    }),
  })

  const minDeclaredValue = (
    weight: number,
    rangeList: RangeDeclareDto[],
    service?: ShipmentService
  ) => {
    let filteredRangeList = rangeList.filter((r) => r.serviceID === service)
    if (filteredRangeList.length === 0) {
      filteredRangeList = rangeList.filter((r) => r.serviceID === 0)
    }

    return (
      filteredRangeList.find(
        (r) => r.minWeight < weight && weight <= r.maxWeight
      )?.minDeclareValue ?? 0
    )
  }

  const user = useAppSelector((state) => state.user)!
  const businessUnitsCatalog = useAppSelector(
    (state) => state.inmutable.businessUnits
  )

  // Mismo hook que usa el formulario de destinatario: la caché por
  // accountSiteID hace que esto no dispare una segunda consulta.
  const { rows: contractDestinationRows } = useContractDestinationBUs(
    user.client?.accountSiteID
  )
  const { options: allowedBusinessUnits, restricted: destinationsRestricted } =
    useMemo(
      () =>
        resolveDestinationBusinessUnits(
          businessUnitsCatalog,
          contractDestinationRows,
          new Date()
        ),
      [businessUnitsCatalog, contractDestinationRows]
    )

  const [initialValidationDone, setInitialValidationDone] = useState(false)

  // Validación al montar el componente
  useEffect(() => {
    if (!user || initialValidationDone) return

    const missingData = []

    if (!user.client?.businessUnit) {
      missingData.push('tienda origen asignada')
    }

    const paymentModeid =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
    const accountBillTo = user.paymentMethod?.filter(
      (x) => x.paymentModeID?.toString() === paymentModeid?.toString()
    )?.[0]?.accountBillTo

    if (
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        accountBillTo === undefined) &&
      paymentModeid.toString() === PaymentModeId.CREDIT.toString()
    ) {
      missingData.push('cuenta para facturar asignada')
    }

    if (missingData.length > 0) {
      setLoadingStatus(ConfirmModalStatus.ERROR)

      if (missingData.length === 2) {
        setLoadingTitle(
          'No tiene cuenta para facturar asignada y no tiene tienda origen asignada. ' +
            'Por favor comuníquese con Soporte Técnico.'
        )
      } else {
        setLoadingTitle(
          `Comuníquese con el departamento de Soporte Técnico. ` +
            `No tiene ${missingData[0]}.`
        )
      }

      setLoading(true)
    }

    setInitialValidationDone(true)
  }, [user, initialValidationDone])

  const shipmentInitialValues = {
    service: undefined,
    paymentModeID:
      user.client?.accountSiteID == 0 || user.client?.accountSiteID == undefined
        ? PaymentModeId.COD
        : PaymentModeId.CREDIT,
    shippingMethod: ShippingMethodEnum.LAND,
    isSafeKeeping:
      (user.client?.accountSiteID == 0 ||
      user.client?.accountSiteID == undefined
        ? PaymentModeId.COD
        : PaymentModeId.CREDIT) === PaymentModeId.COD.toString(),
    packageType: PackageTypeEnum.BOX,
    weightUnit: WeightTypeEnum.KG,
    pieces: [] as PieceInterface[],
    tracking: '',
    observations: '',
    valueDeclarate: '',
    accountBillToID: user!.paymentMethod![0]?.accountBillTo ?? undefined,
    items: [] as ItemInterface[],
    deliveryDistance: 0,
    fileUpload: [],
    serviceData: undefined as ServiceDTO | undefined, // <-- Agrega esto
    declaratedValueInvoceNum: '',
  }
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )

  const senderInitialValues = {
    fullName: user.client?.accountFullName ?? '',
    abbreviationName: identificationTypes.find(
      (t) => t.abbreviationName === user.client?.abbreviationName
    )!,
    identificationNumber: user.client?.identificationNumber ?? '',
  }
  const consigneeInitialValues = {
    abbreviationName: identificationTypes.find(
      (t) => t.abbreviationName === 'V-'
    )!,
    id: '',
    identificationNumber: '',
    fullName: '',
    codePhoneNumber: '',
    phoneId: '',
    phoneTypeId: '',
    phoneNumber: '',
    acceptsSMS: false,
    codeCellPhoneNumber: '',
    cellPhoneNumber: '',
    emailId: '',
    email: '',
    city: '',
    cityCode: '',
    postalCode: '',
    address: '',
    reference: '',
    reset: true,
    displayName: '',
    businessUnit: undefined as BusinessUnitInterface | undefined,
    accountSelected: undefined as AccountInterface | undefined,
    newAccount: undefined,
    ShipmentType: '10' as DeliveryEnum | undefined,
    setAccount: false,
  }
  const [openModal, setOpenModal] = useState(false)
  const [openPrintModal, setOpenPrintModal] = useState(false)
  const [shipmentModal, setShipmentModal] = useState({} as PreShipmentInterface)
  const [shipmentModalNew, setShipmentModalNew] = useState(
    {} as StoreShipmentInterface
  )
  const [status, setStatus] = useState(ConfirmModalStatus.CONFIRM)

  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')

  return (
    <MainPage>
      <PageTitle title="Crear guía" />

      <Formik
        initialValues={{
          sender: senderInitialValues,
          newPiece: newPieceInitialValues,
          consignee: consigneeInitialValues,
          shipment: shipmentInitialValues,
          shippingLocation: {
            name: '',
            code: '',
            address: '',
            postalCode: '',
            coordinates: {
              lat: 0,
              lng: 0,
            },
          },
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          let accountBillTo = user.paymentMethod?.filter(
            (x) =>
              x.paymentModeID.toString() ===
              values.shipment.paymentModeID.toString()
          )![0]?.accountBillTo

          if (
            (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
              accountBillTo === undefined) &&
            values.shipment.paymentModeID.toString() === PaymentModeId.CREDIT
          ) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'Comuniquese con el departamento de Soporte Técnico. No tiene cuenta para facturar asignada'
            )
            setLoading(true)
            return
          }

          if (user.client?.businessUnit === undefined) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'Comuniquese con el departamento de Soporte Técnico. No tiene tienda origen asignado.'
            )
            setLoading(true)
            return
          }
          if (values.shipment.service === undefined) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle('Debes seleccionar un servicio valido.')
            setLoading(true)
            return
          }

          // El contrato pudo cambiar o vencer entre que se eligió la tienda y
          // este submit. Revalidamos para no mandar algo que ApiCore va a
          // rechazar con un 400 crudo (ShipmentController.cs:411-417).
          if (
            destinationsRestricted &&
            values.consignee.businessUnit !== undefined &&
            !allowedBusinessUnits.some(
              (bu) => bu.buCode === values.consignee.businessUnit?.buCode
            )
          ) {
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'La tienda destino ya no está disponible para tu contrato. Selecciona otra.'
            )
            setLoading(true)
            return
          }

          const files =
            values.shipment.fileUpload !== undefined
              ? Array.isArray(values.shipment.fileUpload)
                ? values.shipment.fileUpload
                : [values.shipment.fileUpload]
              : undefined

          const shipmentRequest: ShipmentInterface[] = [
            {
              id: '',
              number: 0,
              accountBillToID:
                values.shipment.paymentModeID.toString() === PaymentModeId.COD
                  ? values.consignee.id === '' ||
                    values.consignee.id === undefined
                    ? undefined
                    : values.consignee.id
                  : accountBillTo === '00000000-0000-0000-0000-000000000000'
                    ? undefined
                    : accountBillTo,
              trackingDetails: values.shipment.tracking,
              pieces: values.shipment.pieces,
              observations: values.shipment.observations,
              totalPieces: values.shipment.pieces.length,
              totalWeight: values.shipment.pieces.reduce(
                (acc, cur) => acc + Number(cur.weight),
                0
              ),
              declaratedValue: values.shipment.pieces.reduce(
                (acc, cur) => acc + Number(cur.declaredValue),
                0
              ),
              tracking: TrackingEnum.TRACKING,
              isHighValueCargo: false,
              service: values.shipment.service,
              deliveryType: values.consignee.ShipmentType as DeliveryEnum,
              shippingMethod: values.shipment.shippingMethod,
              creationUser: user.user?.login ?? 'ccw',
              shipper: user.client!,
              businessUnitOrigin: user.client?.businessUnit!,
              businessUnitConsignee: values.consignee.businessUnit!,
              sender: {
                fullName: values.sender.fullName,
                abbreviationId:
                  values.sender.abbreviationName.taxIdentificationTypeId,
                abbreviationName:
                  values.sender.abbreviationName.taxIdentificationTypeCode,
                identificationNumber: values.sender.identificationNumber,
              },
              consignee: {
                id: values.consignee.id,
                accountCode: '',
                agreementId: 0,
                cityCode: values.consignee.cityCode,
                taxIdentificationTypeID:
                  values.consignee.abbreviationName.taxIdentificationTypeId,
                abbreviationName:
                  values.consignee.abbreviationName.taxIdentificationTypeCode,
                identificationNumber: values.consignee.identificationNumber,
                accountFullName: values.consignee.fullName,
                fiscalAddress: values.consignee.address,
                reference: values.consignee.reference,
                listAccountPhone: [
                  {
                    countryId: '236',
                    acceptsSMS: false,
                    countryPhoneAccessCode: '+58',
                    phoneTypeId: '1',
                    phoneNumber:
                      (values.consignee.codePhoneNumber === 'FIJO'
                        ? ''
                        : values.consignee.codePhoneNumber) +
                      '' +
                      values.consignee.phoneNumber,
                  },
                  {
                    countryId: '236',
                    acceptsSMS: values.consignee.acceptsSMS,
                    countryPhoneAccessCode: '+58',
                    phoneTypeId: '2',
                    phoneNumber:
                      (values.consignee.codePhoneNumber === 'FIJO'
                        ? ''
                        : values.consignee.codeCellPhoneNumber) +
                      ' ' +
                      values.consignee.cellPhoneNumber,
                  },
                ],
                listAccountEmail: [
                  {
                    id: '',
                    emailTypeId: '2',
                    email: values.consignee.email,
                  },
                ],
                listAuthorizingAccount: [],
              },
              shippingLocation:
                values.shippingLocation.name !== ''
                  ? values.shippingLocation
                  : {
                      name: values.consignee.city,
                      address: values.consignee.address,
                      postalCode: values.consignee.postalCode,
                      coordinates:
                        values.consignee.businessUnit?.location.coordinates ??
                        ({} as CoordinatesInterface),
                    },
              isSED: false,
              isToHold: false,
              isFragile: false,
              isRepacke: false,
              isPreAlerted: false,
              isSafeKeeping: values.shipment.isSafeKeeping,

              status: ShipmentStatusEnum.ACTIVE,
              packageType: values.shipment.packageType,
              paymentModeID: values.shipment.paymentModeID,
              files: files,
              declaratedValueInvoceNum:
                values.shipment.declaratedValueInvoceNum,
            },
          ]

          setLoadingStatus(ConfirmModalStatus.PROCESSING)
          setLoadingTitle('Guardando...')
          setLoading(true)

          if (
            values.consignee.id !== undefined &&
            values.consignee.setAccount
          ) {
            let account = {
              accountID: values.consignee.id,
              taxIdentificationTypeID:
                values.consignee?.abbreviationName?.taxIdentificationTypeId,
              identificationNumber: values.consignee.identificationNumber,
              accountFullName: values.consignee.fullName,
              accountPhoneID: values.consignee.phoneId,
              phoneTypeID:
                values.consignee.phoneTypeId === undefined ||
                values.consignee.phoneTypeId.length === 0
                  ? 1
                  : parseInt(values.consignee.phoneTypeId ?? '1'),
              phoneNumber:
                (values.consignee.codePhoneNumber === 'FIJO'
                  ? ''
                  : values.consignee.codePhoneNumber) +
                '' +
                values.consignee.phoneNumber,
              acceptsSMS: values.consignee.acceptsSMS,
              accountEmailID: values.consignee.emailId,
              email: values.consignee.email,
              fiscalAddress: values.consignee.address,
              updateUser: user.user?.login ?? 'ccw',
            } as AccountShipmentDto

            await UpdateAccountShipment(account)
          }
          const newShipment = await createShipmentApi(shipmentRequest, files)

          if (
            newShipment.shipmentNumber !== null &&
            newShipment.shipmentNumber !== ''
          ) {
            resetForm()
            setShipmentModal(newShipment)
            setShipmentModalNew(mapPreShipmentToStoreShipment(newShipment))
            setOpenModal(true)
            setStatus(ConfirmModalStatus.SUCCESS)
            setLoading(false)
          } else {
            setLoading(true)
            setLoadingStatus(ConfirmModalStatus.ERROR)
            setLoadingTitle(
              'Existe un error al crear la guia: ' +
                newShipment.errorMessage +
                ' contacte a Soporte Tecnico.'
            )
          }
        }}
      >
        {(formik) => (
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-1 flex-col gap-16 w-full"
          >
            <div className="flex flex-1 flex-col xl:flex-row gap-16">
              {/* Shipper */}
              <Card title="Datos del Remitente">
                <div className="flex flex-1 flex-col p-4 gap-12">
                  {/* Sender form */}
                  <div className="sm:px-8">
                    <SenderForm formik={formik} />
                  </div>
                  {/* Shipper details */}
                  <div className="flex flex-1 flex-col border rounded px-4 pt-2 pb-6 gap-4 shadow bg-white">
                    <div className="flex flex-1 font-semibold text-sm text-gray-700">
                      Detalles del Remitente
                    </div>

                    <hr className="-mt-2" />

                    <AccountDetails {...user.client!} />
                  </div>
                </div>
              </Card>

              {/* Consignee */}
              <Card title="Datos del Destinatario">
                <div className="flex flex-1 flex-col py-4 mb-6 px-4 sm:px-12">
                  <ConsigneeFormV2 formik={formik} />
                </div>
              </Card>
            </div>
            {formik.values.consignee.id !== '' && (
              <div className="flex flex-1 max-w-full">
                {/* Shipment */}
                <Card title="Datos del Envío" className="max-w-full">
                  <div className="flex flex-1 flex-col px-4 sm:px-12 py-4 mb-6">
                    <ShipmentFormV2 formik={formik} />
                  </div>
                </Card>
              </div>
            )}
            {(shipmentModal?.shipmentNumber?.length ?? 0) > 0 && (
              <ShipmentMessageModal
                open={openModal}
                status={status}
                shipment={shipmentModal}
                setOpen={setOpenModal}
                onPrint={() => {
                  setOpenPrintModal(true)
                  setOpenModal(false)
                }}
              />
            )}

            {(shipmentModal?.shipmentNumber?.length ?? 0) > 0 &&
              openPrintModal && (
                <ShipmentPrintModal
                  open={openPrintModal}
                  shipment={shipmentModalNew}
                  setOpen={setOpenPrintModal}
                  onPrint={() => {}}
                />
              )}

            <ModalLoading
              open={loading}
              title={loadingTitle}
              status={loadingStatus}
              setOpen={setLoading}
              onPrint={() => {}}
            />

            {formik.isSubmitting && (
              <ScrollToError
                formikCount={Object.keys(formik.errors).length > 0}
                data={formik.errors}
              />
            )}
          </form>
        )}
      </Formik>
    </MainPage>
  )
}

export default CreateShipmentV2
